/**
 * Visor de **resultado** del optimizador (réplica de `renderStatsBar` +
 * `renderViewerToolbar` + `sheetSVG` + `renderSideStats` del SSR).
 *
 * Muestra los KPIs globales (tableros, piezas colocadas, % desperdicio, área usada/
 * desperdiciada), la navegación entre tableros (con agrupación de láminas idénticas),
 * el visor SVG del tablero activo (`SheetSvg`) y las estadísticas globales + de la hoja
 * activa. Recibe el resultado y el índice/zoom controlados por el orquestador; no toca
 * la API.
 *
 * Único estado propio: el tamaño medido del contenedor del visor (vía `ResizeObserver`),
 * usado para la escala **fit-to-container** (feature #76) — la lámina llena el visor al
 * cargar en vez de salir diminuta. La aritmética de la escala vive en una función pura
 * y testeable (`computeFitScale` en `../format`); aquí solo se le pasan las medidas
 * reales del DOM. El `zoom` (del orquestador) multiplica sobre esa escala base.
 */
import { useEffect, useRef, useState } from "react";

import { cm2ToM2, computeFitScale } from "../format";
import type { OptimizeResult, Sheet } from "../types";
import { SheetSvg } from "./SheetSvg";

interface ResultViewerProps {
  result: OptimizeResult;
  sheetIdx: number;
  zoom: number;
  onSelectSheet: (idx: number) => void;
  onPrevSheet: () => void;
  onNextSheet: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomReset: () => void;
}

/** KPI individual (réplica de un `tile` de la barra de estadísticas). */
function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="flex flex-col rounded-lg border border-slate-200 bg-white px-3 py-2">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="text-lg font-bold text-slate-800">{value}</span>
      {sub ? <span className="text-xs text-slate-400">{sub}</span> : null}
    </div>
  );
}

/** Clave de una lámina para agrupar consecutivas idénticas (port del SSR). */
function sheetKey(sh: Sheet): string {
  return (
    sh.placements
      .map((p) => `${p.id}:${Math.round(p.x)}:${Math.round(p.y)}`)
      .sort()
      .join("|") + `|${sh.stock.width}x${sh.stock.height}`
  );
}

export function ResultViewer({
  result,
  sheetIdx,
  zoom,
  onSelectSheet,
  onPrevSheet,
  onNextSheet,
  onZoomIn,
  onZoomOut,
  onZoomReset,
}: ResultViewerProps) {
  const sheets = result.sheets;
  const stats = result.stats;
  const unplaced = result.unplaced || [];
  const i = Math.min(sheetIdx, sheets.length - 1);
  const sh = sheets[i];

  const allOk = stats.unplacedParts === 0;

  // ─── Escala fit-to-container (feature #76) ─────────────────────────────────
  // Medimos el contenedor real del SVG (ref + ResizeObserver) y derivamos la escala
  // base para que la lámina llene el visor al cargar, en vez del antiguo 0.32 fijo
  // (que la dejaba diminuta). El `zoom` sigue multiplicando sobre esta base; el botón
  // "reset/100%" vuelve a `zoom=1.0`, es decir, al fit. Se re-ajusta cuando cambia el
  // tamaño del contenedor (ResizeObserver) y cuando cambia la lámina activa (sus
  // dimensiones entran en `computeFitScale`).
  const viewerRef = useRef<HTMLDivElement>(null);
  const [viewerSize, setViewerSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = viewerRef.current;
    if (!el) return;
    // jsdom no implementa ResizeObserver; si falta, nos quedamos con el respaldo
    // que `computeFitScale` aplica ante medidas no fiables (no rompe el render).
    if (typeof ResizeObserver === "undefined") {
      setViewerSize({ width: el.clientWidth, height: el.clientHeight });
      return;
    }
    const ro = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      if (box) setViewerSize({ width: box.width, height: box.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const fitScale = computeFitScale(
    viewerSize.width,
    viewerSize.height,
    sh.stock.width,
    sh.stock.height,
  );
  const scale = fitScale * zoom;

  // Agrupar láminas consecutivas idénticas (port del SheetViewer).
  const groups: Array<{ startIdx: number; count: number; key: string }> = [];
  sheets.forEach((sheet, n) => {
    const k = sheetKey(sheet);
    const last = groups[groups.length - 1];
    if (last && last.key === k) last.count++;
    else groups.push({ startIdx: n, count: 1, key: k });
  });
  const activeGroup = groups.findIndex(
    (g) => i >= g.startIdx && i < g.startIdx + g.count,
  );

  // Estadísticas de la hoja activa.
  const usedM2 = cm2ToM2(sh.usedArea, 4);
  const wasteM2 = cm2ToM2(sh.wasteArea, 4);
  const nCutsH = (sh.cutSegments || []).filter((s) => s.type === "h").length;
  const nCutsV = (sh.cutSegments || []).filter((s) => s.type === "v").length;
  const totalCuts = sheets.reduce(
    (s, x) => s + (x.cutSegments ? x.cutSegments.length : 0),
    0,
  );
  const globalUsedM2 = cm2ToM2(stats.totalUsedArea, 4);
  const globalWasteM2 = cm2ToM2(stats.totalWasteArea, 4);
  const wpct = sh.wastePercent;
  const usedColor = wpct < 25 ? "#22c55e" : wpct < 40 ? "#f97316" : "#ef4444";

  return (
    <div className="flex flex-col gap-4">
      {/* KPIs globales */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        <Kpi label="Tableros" value={String(stats.totalSheets)} sub="usados" />
        <Kpi
          label="Piezas"
          value={`${stats.placedParts} / ${stats.totalParts}`}
          sub={allOk ? "todas colocadas" : `${stats.unplacedParts} sin colocar`}
        />
        <Kpi label="Desperdicio" value={`${stats.wastePercent}%`} />
        <Kpi label="Área usada" value={`${globalUsedM2} m²`} />
        <Kpi label="Desperdicio" value={`${globalWasteM2} m²`} />
      </div>

      {unplaced.length > 0 ? (
        <p
          role="alert"
          className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
        >
          <strong>
            {unplaced.length} pieza{unplaced.length > 1 ? "s" : ""}
          </strong>{" "}
          no caben en el tablero.{" "}
          <span className="font-medium">
            {unplaced.map((p) => p.label).join(", ")}
          </span>
        </p>
      ) : null}

      {/* Barra de navegación de tableros + zoom */}
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
        <button
          type="button"
          aria-label="Tablero anterior"
          disabled={i === 0}
          onClick={onPrevSheet}
          className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm disabled:opacity-40"
        >
          ‹
        </button>
        <div className="flex flex-wrap gap-1">
          {groups.map((g, gi) => (
            <button
              key={g.startIdx}
              type="button"
              aria-label={`Ir al tablero ${g.startIdx + 1}`}
              aria-current={gi === activeGroup ? "true" : undefined}
              onClick={() => onSelectSheet(g.startIdx)}
              className={`rounded-md px-2.5 py-1 text-sm ${
                gi === activeGroup
                  ? "bg-brand text-white"
                  : "border border-slate-300 bg-white text-slate-700"
              }`}
            >
              {g.startIdx + 1}
              {g.count > 1 ? ` ×${g.count}` : ""}
            </button>
          ))}
        </div>
        <button
          type="button"
          aria-label="Tablero siguiente"
          disabled={i === sheets.length - 1}
          onClick={onNextSheet}
          className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm disabled:opacity-40"
        >
          ›
        </button>
        <span className="text-sm text-slate-500">
          Tablero {i + 1} de {sheets.length}
        </span>
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            aria-label="Alejar"
            onClick={onZoomOut}
            className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm"
          >
            −
          </button>
          <button
            type="button"
            aria-label="Restablecer zoom"
            onClick={onZoomReset}
            className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm"
          >
            {Math.round(zoom * 100)}%
          </button>
          <button
            type="button"
            aria-label="Acercar"
            onClick={onZoomIn}
            className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm"
          >
            +
          </button>
        </div>
      </div>

      {/* Visor SVG + estadísticas de la hoja */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_280px]">
        {/* El alto fijo da al fit vertical un área concreta que llenar (sin él, el
            contenedor colapsaría al alto del SVG y el fit no tendría referencia). El
            SVG se centra cuando, ya con zoom, es menor que el área disponible. */}
        <div
          ref={viewerRef}
          className="flex h-[60vh] min-h-[24rem] items-center justify-center overflow-auto rounded-md border border-slate-200 bg-white p-2"
        >
          <SheetSvg sheet={sh} scale={scale} />
        </div>

        <dl className="flex flex-col gap-1 rounded-md border border-slate-200 bg-white p-3 text-sm">
          <div className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-400">
            Estadísticas globales
          </div>
          <StatRow k="Hojas utilizadas" v={`${sh.stock.width}×${sh.stock.height} x${sheets.length}`} />
          <StatRow k="Área total utilizada" v={`${globalUsedM2} m²`} />
          <StatRow k="Área total desperdiciada" v={`${globalWasteM2} m²`} />
          <StatRow k="Cortes totales" v={String(totalCuts)} />
          <StatRow k="% Desperdicio global" v={`${stats.wastePercent}%`} />

          <div className="mb-1 mt-3 text-xs font-bold uppercase tracking-wide text-slate-400">
            Estadísticas de la hoja
          </div>
          <StatRow k="Área utilizada" v={`${usedM2} m²`} />
          <StatRow k="Área desperdiciada" v={`${wasteM2} m²`} />
          <StatRow k="% Desperdicio" v={`${sh.wastePercent}%`} />
          <StatRow k="Paneles" v={String(sh.placements.length)} />
          <StatRow k="Cortes H / V" v={`${nCutsH}H · ${nCutsV}V`} />

          <div className="mt-3">
            <div className="mb-1 flex justify-between text-xs text-slate-400">
              <span>Aprovechado</span>
              <span>Desperdicio</span>
            </div>
            <div className="flex h-3 overflow-hidden rounded-full bg-amber-50">
              <div style={{ width: `${100 - wpct}%`, background: usedColor }} />
              <div className="flex-1 bg-amber-50" />
            </div>
            <div className="mt-1 flex justify-between text-xs font-semibold">
              <span className="text-green-600">{100 - wpct}%</span>
              <span className="text-amber-600">{wpct}%</span>
            </div>
          </div>
        </dl>
      </div>
    </div>
  );
}

/** Fila clave/valor de las estadísticas. */
function StatRow({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-slate-500">{k}</dt>
      <dd className="font-medium text-slate-700">{v}</dd>
    </div>
  );
}
